import { useState } from 'react';
import { Form, Card, Row, Col, Button, Image } from 'react-bootstrap';
import ProfileMobileSectionActions from './ProfileMobileSectionActions';
import Select from 'react-select';
import ResponsiveMultiSelect from './components/Profile/ResponsiveMultiSelect';
import studyDirectionsData from './data/studyDirections.json';
import europeanCountries from './data/europeanCountries.js';
import icDiploma from './assets/img/ic-diploma.svg';
import icUniversity from './assets/img/ic-university.svg';
import icBook from './assets/img/ic-book.svg';
import icValidationError from './assets/img/ic-danger.svg';


const UserAddAcademics = ({ formData, handleChange, handleAcademicsSubmit, isEditPage, hideEdit, errors = {} }) => {

    const [isEditing, setIsEditing] = useState(!isEditPage); // ✅ If adding, fields are enabled
    const [tempFormData, setTempFormData] = useState({ ...formData }); // ✅ Store initial values

    const countryOptions = europeanCountries;

    const countryName =
        countryOptions.find(c => String(c.value) === String(formData.academicsCountry))?.label
    || "N/A";

    const handleCountryChange = (option) => {
        handleChange({
            target: {
                name: 'academicsCountry',
                value: option?.value || '',
            },
        });
    };
    
    // ✅ Enable editing mode
    const handleEditClick = () => {
        setIsEditing(true);
        setTempFormData({ ...formData }); // ✅ Store previous values
    };
    
    // ✅ Save changes and disable editing
    const handleSaveClick = async (e) => {
        e.preventDefault();
        if (typeof handleAcademicsSubmit === 'function') { // ✅ Ensure it's a function before calling
            await handleAcademicsSubmit(e);
            setIsEditing(false); // ✅ Exit edit mode after saving
        } else {
            console.error('Error: handleAcademicsSubmit is not a function.');
        }
    };
    
    // ✅ Cancel editing and restore previous values
    const handleCancelClick = () => {
        setIsEditing(false);
        Object.keys(tempFormData).forEach((key) => {
            handleChange({ target: { name: key, value: tempFormData[key] } });
        });
    };

    const studyDirectionOptionsGrouped = Object.entries(studyDirectionsData).map(
        ([category, directions]) => ({
            label: category,
            options: directions.map((d) => ({
                label: d,
                value: d,
            })),
        })
    );

    const studyDirLabelByValue = studyDirectionOptionsGrouped
        .flatMap(g => g.options)
        .reduce((acc, opt) => {
            acc[String(opt.value)] = opt.label;
            return acc;
    }, {});

    const countryError = errors.academicsCountry;

    return (
        <>
            <Card>
                <Card.Body>
                    <Row>
                        <Col md={12}>
                            <div className="profile-section-heading-row">
                            {isEditPage && !hideEdit && (
                                <div className="float-end d-none d-lg-block">
                                    {!isEditing ? (
                                        <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                    ) : (
                                        <>
                                            <Button variant="outline-secondary" className="btn-sm me-2" onClick={handleCancelClick}>Cancel</Button>
                                            <Button variant="primary" className="btn-sm" onClick={handleSaveClick}>Save</Button>
                                        </>
                                    )}
                                </div>
                            )}
                            <h3 className="company-detail-section-title d-lg-none"><span className="item-number">1.</span> Education info</h3>
                            <h3 className="d-none d-lg-flex"><span className="item-number">1.</span>Education info</h3>
                            </div>
                            <hr />
                        </Col>
                    </Row>
                    <>
                        {isEditing ? (
                            <>
                                <Row className="mb-3 profile-edit-fields">
                                    <Col md={3}> 
                                        {/* Current degree */}
                                        <Form.Group className="validated" controlId="currentDegree">
                                            <Form.Label>Current degree <span>*</span></Form.Label>
                                            <Form.Select
                                                name="currentDegree"
                                                value={formData.currentDegree || ''}
                                                isInvalid={!!errors.currentDegree}
                                                onChange={(e) => {
                                                    const value = e.target.value;

                                                    handleChange({
                                                        target: {
                                                            name: 'currentDegree',
                                                            value,
                                                        },
                                                    });

                                                    handleChange({
                                                        target: {
                                                            name: 'highestDegree',
                                                            value,
                                                        },
                                                    });
                                                }}
                                            >
                                                <option value="">Select degree...</option>
                                                <option value="No degree">No degree</option>
                                                <option value="High school diploma">High school diploma</option>
                                                <option value="Vocational qualification">Vocational qualification</option>
                                                <option value="Associate degree">Associate degree</option>
                                                <option value="Bachelor's degree">Bachelor's degree</option>
                                                <option value="Master's degree">Master's degree</option>
                                                <option value="Doctorate (PhD)">Doctorate (PhD)</option>
                                                <option value="Professional degree">Professional degree</option>
                                                <option value="Postdoctoral qualification">Postdoctoral qualification</option>
                                                <option value="Other">Other</option>
                                            </Form.Select>
                                            {errors.currentDegree && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img src={icValidationError} alt={errors.currentDegree} className="custom-error-icon" />
                                                    <div className="custom-tooltip">{errors.currentDegree}</div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}> 
                                        {/* Educational institution */}
                                        <Form.Group className="validated" controlId="currentEducationalInstitution">
                                            <Form.Label>Educational institution <span>*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="currentEducationalInstitution"
                                                value={formData.currentEducationalInstitution || ''}
                                                onChange={handleChange}
                                                placeholder="Enter educational institution"
                                                isInvalid={!!errors.currentEducationalInstitution}
                                            />
                                            {errors.currentEducationalInstitution && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img src={icValidationError} alt={errors.currentEducationalInstitution} className="custom-error-icon" />
                                                    <div className="custom-tooltip">{errors.currentEducationalInstitution}</div>
                                                </div>
                                            )}
                                        </Form.Group>

                                    </Col>
                                    <Col md={3}> 
                                        {/* Country and manually entered education city */}
                                        <Form.Group className="validated mb-3" controlId="academicsCountry">
                                            <Form.Label>Country <span>*</span></Form.Label>
                                            <Select
                                                className={`react-select ${countryError ? 'is-invalid' : ''}`}
                                                classNamePrefix="react-select"
                                                isDisabled={!isEditing}
                                                value={countryOptions.find((c) => c.value === formData.academicsCountry) || null}
                                                onChange={handleCountryChange}
                                                options={countryOptions}
                                            />
                                            {countryError && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img src={icValidationError} alt={countryError} className="custom-error-icon" />
                                                    <div className="custom-tooltip">{countryError}</div>
                                                </div>
                                            )}
                                        </Form.Group>

                                        <Form.Group className="validated mb-3" controlId="academicsCity">
                                            <Form.Label>City <span>*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="academicsCity"
                                                value={formData.academicsCity || ''}
                                                onChange={handleChange}
                                                placeholder="Enter city"
                                                isInvalid={!!errors.academicsCity}
                                                required
                                            />
                                            {errors.academicsCity && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img src={icValidationError} alt={errors.academicsCity} className="custom-error-icon" />
                                                    <div className="custom-tooltip">{errors.academicsCity}</div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}> 
                                        {/* Study direction */}
                                        <Form.Group className="validated" controlId="academicsStudyDirection">
                                            <Form.Label>Study direction <span>*</span></Form.Label>
                                            <ResponsiveMultiSelect
                                                label="Study direction"
                                                name="academicsStudyDirection"
                                                inputId="academicsStudyDirection"
                                                className={`react-select ${errors.academicsStudyDirection ? 'is-invalid' : ''}`}
                                                classNamePrefix="react-select"
                                                placeholder="Select study directions"
                                                searchPlaceholder="Search study directions..."
                                                value={
                                                    (formData.academicsStudyDirection || []).map((val) => ({
                                                        label: val,
                                                        value: val,
                                                    }))
                                                }
                                                onChange={(selected) =>
                                                    handleChange({
                                                        target: {
                                                            name: 'academicsStudyDirection',
                                                            value: selected.map((opt) => opt.value),
                                                        },
                                                    })
                                                }
                                                options={studyDirectionOptionsGrouped} // ✅ Now using grouped options
                                                isDisabled={!isEditing}
                                            />
                                            {errors.academicsStudyDirection && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img src={icValidationError} alt={errors.academicsStudyDirection} className="custom-error-icon" />
                                                    <div className="custom-tooltip">{errors.academicsStudyDirection}</div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </>
                        ): (
                            // Not editing
                            <>
                                <Row className="mb-3">
                                    <Col md={3}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icDiploma} alt="Current degree" />
                                            </span>
                                            <span className="text">
                                                Current degree
                                                <br />
                                                <strong>
                                                    {formData.currentDegree || formData.highestDegree || "N/A"}
                                                </strong>
                                            </span>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icUniversity} alt="Educational institution" />
                                            </span>
                                            <span className="text">
                                                Educational institution
                                                <br />
                                                <strong>
                                                    {formData.currentEducationalInstitution || "N/A"}
                                                </strong>
                                            </span>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icUniversity} alt="City & Country" />
                                            </span>
                                            <span className="text">
                                                City & Country
                                                <br />
                                                <strong>
                                                    {countryName}, {formData.academicsCity || "N/A"}
                                                </strong>
                                            </span>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icBook} alt="Study direction" />
                                            </span>
                                            <span className="text">
                                                Study direction
                                                <br />
                                                <strong>
                                                    {Array.isArray(formData.academicsStudyDirection)
                                                        ? formData.academicsStudyDirection
                                                            .map(v => studyDirLabelByValue[String(v)] ?? String(v))
                                                            .join(', ')
                                                        : (studyDirLabelByValue[String(formData.academicsStudyDirection)] 
                                                            ?? (formData.academicsStudyDirection || 'N/A'))}
                                                </strong>
                                            </span>
                                        </div>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </>
                    
                    <ProfileMobileSectionActions
                        isEditing={isEditing}
                        onEdit={handleEditClick}
                        onCancel={handleCancelClick}
                        onSave={handleSaveClick}
                        show={isEditPage && !hideEdit}
                    />
                </Card.Body>
            </Card>
        </>
    );
};

export default UserAddAcademics;
